"use client";

// ClawHub API — fetches skills from backend, falls back to local mocks.
import { API_BASE } from "./api/config";
import { getToken } from "./api/client";

import type {
  ClawHubSkill,
  ClawHubTag,
  SkillsResponse,
  TagsResponse,
  SkillCategoryItem,
  CategoriesResponse,
  ListSkillTagsResp,
  ListSkillItem,
  ListSkillResponse,
  ListSkillCategoriesResp,
  SkillInteractionReq,
  SkillInteractionResp,
} from "@zrt-ai-office/types";

export type {
  ClawHubSkill,
  ClawHubTag,
  SkillsResponse,
  TagsResponse,
  SkillCategoryItem,
  CategoriesResponse,
  ListSkillTagsResp,
  ListSkillItem,
  ListSkillResponse,
  ListSkillCategoriesResp,
  SkillInteractionReq,
  SkillInteractionResp,
};

// Realistic mock data based on actual ClawHub skills
const MOCK_SKILLS: ClawHubSkill[] = [
  {
    id: "desktop-commander",
    name: "Desktop Commander",
    description:
      "Terminal and file management for LLM-based AI agents. Execute commands, manage files, search codebases, and edit files with surgical precision.",
    author: "wonderwhy-er",
    authorVerified: true,
    downloads: 892456,
    installs: 156789,
    stars: 1247,
    version: "0.1.19",
    lastUpdated: "2 天前",
    tags: ["terminal", "files", "search", "edit"],
    category: "dev",
    homepage: "https://github.com/wonderwhy-er/DesktopCommanderMCP",
  },
  {
    id: "browser-use",
    name: "Browser Use",
    description:
      "AI-powered browser automation. Navigate websites, fill forms, extract data, and interact with web pages naturally through AI commands.",
    author: "browser-use",
    authorVerified: true,
    downloads: 756123,
    installs: 134567,
    stars: 2156,
    version: "0.3.2",
    lastUpdated: "1 天前",
    tags: ["browser", "automation", "web", "scraping"],
    category: "automation",
  },
  {
    id: "firecrawl",
    name: "Firecrawl",
    description:
      "Turn any website into clean, LLM-ready markdown or structured data. Powerful web scraping and crawling with built-in AI optimization.",
    author: "mendableai",
    authorVerified: true,
    downloads: 645892,
    installs: 98765,
    stars: 1876,
    version: "1.2.0",
    lastUpdated: "3 天前",
    tags: ["web", "scraping", "markdown", "data"],
    category: "data",
  },
  {
    id: "context7",
    name: "Context7",
    description:
      "Pulls up-to-date, version-specific documentation and code examples directly from official sources. Always accurate, never hallucinated.",
    author: "upstash",
    authorVerified: true,
    downloads: 534267,
    installs: 87654,
    stars: 943,
    version: "1.0.8",
    lastUpdated: "1 周前",
    tags: ["documentation", "context", "accuracy", "search"],
    category: "dev",
  },
  {
    id: "magic-mcp",
    name: "Magic MCP",
    description:
      "Create and execute tools on-the-fly with AI. Dynamic tool generation that adapts to any task without predefined schemas.",
    author: "21st-dev",
    authorVerified: true,
    downloads: 423156,
    installs: 76543,
    stars: 1532,
    version: "0.4.1",
    lastUpdated: "4 天前",
    tags: ["dynamic", "tools", "generation", "flexible"],
    category: "ai",
  },
  {
    id: "mcp-notion",
    name: "MCP Notion",
    description:
      "Full Notion API integration. Search, read, create, and update Notion pages, databases, and blocks with natural language.",
    author: "suekou",
    authorVerified: true,
    downloads: 387654,
    installs: 65432,
    stars: 876,
    version: "0.6.2",
    lastUpdated: "5 天前",
    tags: ["notion", "productivity", "notes", "database"],
    category: "productivity",
  },
  {
    id: "tavily-search",
    name: "Tavily Search",
    description:
      "AI-optimized search engine that returns clean, structured results. Built specifically for LLM consumption with source attribution.",
    author: "tavily",
    authorVerified: true,
    downloads: 356789,
    installs: 54321,
    stars: 1123,
    version: "0.2.3",
    lastUpdated: "2 天前",
    tags: ["search", "web", "ai", "structured"],
    category: "web",
  },
  {
    id: "sequential-thinking",
    name: "Sequential Thinking",
    description:
      "Dynamic problem-solving through structured thought sequences. Break down complex problems into manageable reasoning steps.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 298765,
    installs: 43210,
    stars: 654,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["thinking", "reasoning", "problem-solving", "chain-of-thought"],
    category: "ai",
  },
  {
    id: "github-mcp",
    name: "GitHub MCP",
    description:
      "Complete GitHub integration. Manage repositories, issues, PRs, and code searches directly through AI conversations.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 276543,
    installs: 38765,
    stars: 987,
    version: "0.6.2",
    lastUpdated: "3 天前",
    tags: ["github", "git", "code", "repository"],
    category: "dev",
  },
  {
    id: "playwright-mcp",
    name: "Playwright MCP",
    description:
      "Browser automation powered by Playwright. Control browsers, take screenshots, and interact with web applications programmatically.",
    author: "executeautomation",
    authorVerified: false,
    downloads: 245678,
    installs: 34567,
    stars: 765,
    version: "0.3.15",
    lastUpdated: "4 天前",
    tags: ["browser", "automation", "testing", "playwright"],
    category: "automation",
  },
  {
    id: "memory-mcp",
    name: "Memory MCP",
    description:
      "Persistent memory for AI agents. Store, retrieve, and manage contextual information across conversations and sessions.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 234567,
    installs: 32109,
    stars: 543,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["memory", "persistence", "context", "storage"],
    category: "ai",
  },
  {
    id: "puppeteer-mcp",
    name: "Puppeteer MCP",
    description:
      "Headless Chrome automation for AI. Navigate, screenshot, and interact with web pages using Puppeteer through natural language.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 213456,
    installs: 29876,
    stars: 456,
    version: "0.6.2",
    lastUpdated: "5 天前",
    tags: ["browser", "chrome", "automation", "headless"],
    category: "automation",
  },
  {
    id: "slack-mcp",
    name: "Slack MCP",
    description:
      "Full Slack workspace integration. Send messages, manage channels, search history, and interact with Slack workspaces via AI.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 198765,
    installs: 27654,
    stars: 432,
    version: "0.6.2",
    lastUpdated: "6 天前",
    tags: ["slack", "chat", "communication", "team"],
    category: "communication",
  },
  {
    id: "filesystem-mcp",
    name: "Filesystem MCP",
    description:
      "Safe, sandboxed file system access. Read, write, and manage files with configurable permissions and security boundaries.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 187654,
    installs: 25432,
    stars: 321,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["files", "filesystem", "read", "write"],
    category: "file",
  },
  {
    id: "everything-mcp",
    name: "Everything MCP",
    description:
      "Ultra-fast file search using Everything engine. Find any file on your system instantly with fuzzy matching and regex support.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 176543,
    installs: 23456,
    stars: 298,
    version: "0.6.2",
    lastUpdated: "2 周前",
    tags: ["search", "files", "fast", "everything"],
    category: "file",
  },
  {
    id: "exa-mcp",
    name: "Exa Search",
    description:
      "Neural search engine for AI applications. Find similar content, answers, and insights using embeddings-based semantic search.",
    author: "exa-labs",
    authorVerified: true,
    downloads: 165432,
    installs: 21345,
    stars: 567,
    version: "1.0.3",
    lastUpdated: "3 天前",
    tags: ["search", "semantic", "embeddings", "neural"],
    category: "ai",
  },
  {
    id: "postgres-mcp",
    name: "Postgres MCP",
    description:
      "Direct PostgreSQL database access. Query, insert, update, and manage your database with natural language SQL generation.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 154321,
    installs: 19876,
    stars: 345,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["database", "postgres", "sql", "query"],
    category: "data",
  },
  {
    id: "sqlite-mcp",
    name: "SQLite MCP",
    description:
      "Lightweight SQLite database operations. Create, query, and manage SQLite databases with AI-assisted schema design.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 143210,
    installs: 18765,
    stars: 234,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["database", "sqlite", "sql", "lightweight"],
    category: "data",
  },
  {
    id: "brave-search",
    name: "Brave Search",
    description:
      "Privacy-focused web search via Brave. Get search results without tracking, with optional image and news search capabilities.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 132109,
    installs: 17654,
    stars: 287,
    version: "0.6.2",
    lastUpdated: "4 天前",
    tags: ["search", "privacy", "web", "brave"],
    category: "web",
  },
  {
    id: "google-maps-mcp",
    name: "Google Maps MCP",
    description:
      "Google Maps integration for AI. Search places, get directions, calculate distances, and explore locations through conversation.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 121098,
    installs: 16543,
    stars: 198,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["maps", "location", "directions", "google"],
    category: "web",
  },
  {
    id: "linear-mcp",
    name: "Linear MCP",
    description:
      "Linear issue tracker integration. Create, update, and manage issues, projects, and cycles with AI-powered workflows.",
    author: "jerhadf",
    authorVerified: false,
    downloads: 109876,
    installs: 15432,
    stars: 312,
    version: "0.2.1",
    lastUpdated: "5 天前",
    tags: ["linear", "issues", "project", "management"],
    category: "productivity",
  },
  {
    id: "cloudflare-mcp",
    name: "Cloudflare MCP",
    description:
      "Manage Cloudflare resources. Control Workers, Pages, DNS, and more through AI-driven infrastructure management.",
    author: "cloudflare",
    authorVerified: true,
    downloads: 98765,
    installs: 14321,
    stars: 267,
    version: "0.1.5",
    lastUpdated: "3 天前",
    tags: ["cloudflare", "workers", "dns", "infrastructure"],
    category: "dev",
  },
  {
    id: "docker-mcp",
    name: "Docker MCP",
    description:
      "Docker container management. Build, run, and manage containers and images with AI-assisted DevOps workflows.",
    author: "docker",
    authorVerified: true,
    downloads: 87654,
    installs: 13210,
    stars: 234,
    version: "0.1.3",
    lastUpdated: "1 周前",
    tags: ["docker", "containers", "devops", "infrastructure"],
    category: "dev",
  },
  {
    id: "aws-mcp",
    name: "AWS MCP",
    description:
      "Amazon Web Services integration. Manage S3, EC2, Lambda, and other AWS resources through natural language commands.",
    author: "aws",
    authorVerified: true,
    downloads: 76543,
    installs: 12109,
    stars: 198,
    version: "0.2.0",
    lastUpdated: "4 天前",
    tags: ["aws", "cloud", "s3", "lambda"],
    category: "dev",
  },
  {
    id: "stripe-mcp",
    name: "Stripe MCP",
    description:
      "Stripe payments integration. Create customers, manage subscriptions, and process payments with AI assistance.",
    author: "stripe",
    authorVerified: true,
    downloads: 65432,
    installs: 10987,
    stars: 176,
    version: "0.1.2",
    lastUpdated: "6 天前",
    tags: ["stripe", "payments", "billing", "subscriptions"],
    category: "productivity",
  },
  {
    id: "vercel-mcp",
    name: "Vercel MCP",
    description:
      "Deploy and manage Vercel projects. Control deployments, domains, and environment variables through AI workflows.",
    author: "vercel",
    authorVerified: true,
    downloads: 54321,
    installs: 9876,
    stars: 154,
    version: "0.1.1",
    lastUpdated: "2 天前",
    tags: ["vercel", "deploy", "hosting", "serverless"],
    category: "dev",
  },
  {
    id: "openai-mcp",
    name: "OpenAI MCP",
    description:
      "Direct OpenAI API access. Generate text, images, embeddings, and more with AI-managed token optimization.",
    author: "openai",
    authorVerified: true,
    downloads: 43210,
    installs: 8765,
    stars: 132,
    version: "0.2.4",
    lastUpdated: "1 周前",
    tags: ["openai", "gpt", "images", "embeddings"],
    category: "ai",
  },
  {
    id: "anthropic-mcp",
    name: "Anthropic MCP",
    description:
      "Anthropic Claude API integration. Access Claude models with optimized context management and streaming support.",
    author: "anthropic",
    authorVerified: true,
    downloads: 32109,
    installs: 7654,
    stars: 98,
    version: "0.1.3",
    lastUpdated: "5 天前",
    tags: ["anthropic", "claude", "llm", "api"],
    category: "ai",
  },
  {
    id: "youtube-mcp",
    name: "YouTube MCP",
    description:
      "YouTube video processing. Extract transcripts, analyze content, search videos, and manage playlists with AI.",
    author: "modelcontextprotocol",
    authorVerified: true,
    downloads: 21098,
    installs: 6543,
    stars: 87,
    version: "0.6.2",
    lastUpdated: "1 周前",
    tags: ["youtube", "video", "transcript", "media"],
    category: "media",
  },
  {
    id: "pdf-reader",
    name: "PDF Reader MCP",
    description:
      "Extract and analyze PDF documents. Read text, tables, and images from PDFs with intelligent content parsing.",
    author: "adobe",
    authorVerified: true,
    downloads: 19876,
    installs: 5432,
    stars: 76,
    version: "0.1.0",
    lastUpdated: "3 天前",
    tags: ["pdf", "documents", "extract", "read"],
    category: "file",
  },
];

const TAG_ZH: Record<string, string> = {
  search: "搜索",
  web: "网页",
  files: "文件",
  browser: "浏览器",
  automation: "自动化",
  database: "数据库",
  scraping: "数据采集",
  context: "上下文",
  ai: "AI",
  code: "编程",
  git: "Git",
  pdf: "PDF",
  documents: "文档",
  extract: "提取",
  read: "阅读",
  api: "API",
  cloud: "云服务",
  productivity: "效率",
  communication: "通讯",
  data: "数据",
  image: "图片",
  video: "视频",
  audio: "音频",
  monitoring: "监控",
  testing: "测试",
  security: "安全",
  analytics: "分析",
  email: "邮件",
  calendar: "日历",
  notes: "笔记",
};

const extractTags = (skills: ClawHubSkill[]): ClawHubTag[] => {
  const tagCounts: Record<string, number> = {};
  skills.forEach((skill) => {
    skill.tags.forEach((tag) => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });
  return Object.entries(tagCounts)
    .map(([name, count]) => ({ id: name, name, label: TAG_ZH[name], count }))
    .sort((a, b) => b.count - a.count);
};

const MOCK_TAGS = extractTags(MOCK_SKILLS);

// Categories mapping
export const CLAWHUB_CATEGORIES = [
  { id: "all", name: "全部" },
  { id: "ai", name: "AI/ML" },
  { id: "dev", name: "开发工具" },
  { id: "data", name: "数据处理" },
  { id: "automation", name: "自动化" },
  { id: "productivity", name: "效率工具" },
  { id: "communication", name: "通讯协作" },
  { id: "web", name: "网页工具" },
  { id: "file", name: "文件管理" },
  { id: "media", name: "媒体处理" },
];

// Simulated API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch skills — real backend API with mock fallback.
 *
 * With search query:  POST /skill/api/v1/search (semantic)
 * Without search:     GET  /skill/api/v1/list  (paginated)
 *
 * Falls back to local mocks if backend is unreachable.
 */
/** 将前端 SortValue 映射为接口的 sortBy / sortOrder 参数 */
function buildSortParams(sort: "composite" | "download" | "latest"): {
  sortBy?: string;
  sortOrder?: string;
} {
  switch (sort) {
    case "download":
      return { sortBy: "download", sortOrder: "desc" };
    case "composite":
      return { sortBy: "composite", sortOrder: "desc" };
    case "latest":
      return { sortBy: "latest", sortOrder: "desc" };
    default:
      return {};
  }
}

export async function fetchSkills({
  page = 1,
  pageSize = 12,
  category = "all",
  tag = "",
  keyword = "",
  sort = "composite",
}: {
  page?: number;
  pageSize?: number;
  category?: string;
  /** 标签过滤，对应接口 tags 参数（逗号分隔） */
  tag?: string;
  /** 全文搜索关键词，对应接口 keyword 参数 */
  keyword?: string;
  sort?: "composite" | "download" | "latest";
}): Promise<SkillsResponse> {
  const keywordTrimmed = keyword.trim();

  try {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (category && category !== "all") params.set("category", category);
    if (tag) params.set("tags", tag);
    if (keywordTrimmed) params.set("keyword", keywordTrimmed);
    const { sortBy, sortOrder } = buildSortParams(sort);
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);

    const token = getToken();
    const res = await fetch(
      `${API_BASE}/skill/api/v1/list?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (res.ok) {
      const raw = await res.json();
      const payload: ListSkillResponse = raw?.data ?? raw;
      const items = Array.isArray(payload.items) ? payload.items : [];
      const mapped = items.map(mapBackendSkill);
      const total = payload.total ?? mapped.length;
      return {
        skills: mapped,
        total,
        page: payload.page ?? page,
        pageSize: payload.pageSize ?? pageSize,
        hasMore: page * pageSize < total,
      };
    }
  } catch {
    // fall through to mock
  }

  // ── Fallback: Local mock ──
  await delay(300 + Math.random() * 200);

  let filtered = [...MOCK_SKILLS];

  if (category && category !== "all") {
    filtered = filtered.filter((s) => s.category === category);
  }
  if (tag) {
    filtered = filtered.filter((s) => s.tags.includes(tag));
  }
  if (keywordTrimmed) {
    const q = keywordTrimmed.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q)) ||
        s.author.toLowerCase().includes(q),
    );
  }

  if (sort === "download") {
    filtered.sort((a, b) => b.downloads - a.downloads);
  } else if (sort === "composite") {
    filtered.sort((a, b) => b.stars - a.stars);
  }

  const total = filtered.length;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    skills: filtered.slice(start, end),
    total,
    page,
    pageSize,
    hasMore: end < total,
  };
}

/**
 * Map ListSkillItem (GET /skill/api/v1/list) to our ClawHubSkill shape.
 */
function mapBackendSkill(entry: ListSkillItem): ClawHubSkill {
  const meta: Record<string, unknown> = entry.metadata ?? {};
  return {
    id: String(entry.id),
    skillKey: entry.skillKey,
    name: entry.name,
    description: entry.description,
    author: entry.author,
    authorVerified: entry.publisherType === "official",
    downloads: entry.downloadNum,
    installs: entry.useNum,
    stars: entry.likeNum,
    collectNum: entry.collectNum,
    version: entry.version,
    lastUpdated: entry.updatedAt
      ? new Date(entry.updatedAt).toLocaleDateString()
      : "—",
    tags: parseTags(entry.tags),
    category: entry.category,
    isPaid: entry.isPaid,
    price: entry.price,
    homepage:
      typeof meta.homepage === "string"
        ? meta.homepage
        : entry.repoUrl || undefined,
    repository: entry.repoUrl || undefined,
    iconEmoji: typeof meta.emoji === "string" ? meta.emoji : undefined,
    avatarUrl: typeof meta.avatarUrl === "string" ? meta.avatarUrl : undefined,
    isCollect: entry.isCollect,
    isLike: entry.isLike,
    isDownload: entry.isDownload,
    isUse: entry.isUse,
    isBuy: entry.isBuy,
  };
}

/** Parse tags from comma-separated string or array */
function parseTags(tags: unknown): string[] {
  if (Array.isArray(tags)) return tags.map(String);
  if (typeof tags === "string" && tags.trim())
    return tags.split(",").map((t) => t.trim());
  return [];
}

/**
 * Fetch popular tags — GET /skill/api/v1/tags, falls back to mock.
 */
export async function fetchTags(): Promise<TagsResponse> {
  try {
    const res = await fetch(`${API_BASE}/skill/api/v1/tags`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (res.ok) {
      const raw = await res.json();
      const payload: ListSkillTagsResp = raw?.data ?? raw;
      const items = Array.isArray(payload.items) ? payload.items : [];
      if (items.length > 0) {
        const tags: ClawHubTag[] = items.map((item) => ({
          id: item.tag,
          name: item.tag,
          label: TAG_ZH[item.tag],
          count: item.count,
        }));
        return { tags };
      }
    }
  } catch {
    // fall through to mock
  }

  // Mock fallback
  await delay(100);
  return { tags: MOCK_TAGS.slice(0, 20) };
}

/**
 * Fetch skill categories — GET /skill/api/v1/categories
 * Returns list of categories with counts, falls back to extracting tags from skills.
 */
/**
 * Fetch skill categories — GET /skill/api/v1/categories
 * 返回分类列表，含首位“全部”项。
 */
export async function fetchCategories(): Promise<CategoriesResponse> {
  const ALL_ITEM: SkillCategoryItem = {
    id: 0,
    value: "all",
    label: "全部",
    count: 0,
    createdAt: "",
    updatedAt: "",
  };
  try {
    const res = await fetch(`${API_BASE}/skill/api/v1/categories`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (res.ok) {
      const raw = await res.json();
      const payload: ListSkillCategoriesResp = raw?.data ?? raw;
      const items = Array.isArray(payload.items) ? payload.items : [];
      if (items.length > 0) {
        return { items: [ALL_ITEM, ...items] };
      }
    }
  } catch {
    // fall through to fallback
  }

  // Fallback
  const fallback: SkillCategoryItem[] = [
    {
      id: 1,
      value: "ai",
      label: "AI/ML",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 2,
      value: "dev",
      label: "开发工具",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 3,
      value: "data",
      label: "数据处理",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 4,
      value: "automation",
      label: "自动化",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 5,
      value: "productivity",
      label: "效率工具",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 6,
      value: "web",
      label: "网页工具",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 7,
      value: "file",
      label: "文件管理",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
    {
      id: 8,
      value: "media",
      label: "媒体处理",
      count: 0,
      createdAt: "",
      updatedAt: "",
    },
  ];
  return { items: [ALL_ITEM, ...fallback] };
}

/**
 * Fetch a single skill by ID — tries backend first, falls back to mock.
 */
export async function fetchSkillById(id: string): Promise<ClawHubSkill | null> {
  try {
    // Try to find by listing with name filter or getting by ID
    const token = getToken();
    const res = await fetch(
      `${API_BASE}/skill/api/v1/list?page=1&pageSize=50`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (res.ok) {
      const raw = await res.json();
      const payload = raw?.data ?? raw;
      const items = Array.isArray(payload.items) ? payload.items : [];
      const found = items.find(
        (item: any) => item.id === id || item.slug === id,
      ); // eslint-disable-line @typescript-eslint/no-explicit-any
      if (found) return mapBackendSkill(found);
    }
  } catch {
    // fall through to mock
  }

  // Mock fallback
  await delay(150);
  return MOCK_SKILLS.find((s) => s.id === id) || null;
}

/**
 * Format download count for display
 */
export function formatDownloads(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

/**
 * 技能互动 — POST /skill/api/v1/interaction
 * 需要登录（token 从 localStorage 自动读取）
 */
export async function skillInteraction(
  req: SkillInteractionReq,
): Promise<SkillInteractionResp> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/skill/api/v1/interaction`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    if (res.status === 429) {
      const body = await res.json().catch(() => ({ msg: "操作太频繁，请稍后再试" }));
      throw new Error(body?.msg ?? "操作太频繁，请稍后再试");
    }
    throw new Error(`skillInteraction failed: ${res.status}`);
  }
  const raw = await res.json();
  return (raw?.data ?? raw) as SkillInteractionResp;
}
